import React, { useEffect, useState, useCallback } from 'react';
import { UserLayout } from '../../components/layout/UserLayout';
import { userAPI, publicAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Wallet, ArrowUpRight, ArrowDownRight, Activity, TrendingUp, Radio, Coins, Sprout, PiggyBank } from 'lucide-react';
import { formatCurrency, formatDate, convertAmount } from '../../utils/helpers';
import { toast } from 'sonner';
import { useWebSocket } from '../../hooks/useWebSocket';
import { useAuth } from '../../contexts/AuthContext';

export const UserDashboard = () => {
  const { user } = useAuth();
  const defaultCurrency = user?.default_currency || null;
  const [wallets, setWallets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [savingsAccounts, setSavingsAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [updatedCurrency, setUpdatedCurrency] = useState(null);

  const handleWsMessage = useCallback((data) => {
    if (data.type === 'wallet_update') {
      setWsConnected(true);
      setWallets(prev => prev.map(w => 
        w.currency === data.currency ? { ...w, balance: data.balance } : w
      ));
      setUpdatedCurrency(data.currency);
      setTimeout(() => setUpdatedCurrency(null), 2000);
      toast.success(`${data.currency} balance updated: ${data.balance.toFixed(2)}`);
      // Refresh transactions
      userAPI.getTransactions(0, 10).then(res => setTransactions(res.data));
    }
  }, []);

  useWebSocket(handleWsMessage);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [walletsRes, transactionsRes, ratesRes, savingsRes] = await Promise.all([
        userAPI.getWallets(),
        userAPI.getTransactions(0, 10),
        publicAPI.getExchangeRates(),
        userAPI.listSavingsAccounts().catch(() => ({ data: [] })),
      ]);
      setWallets(walletsRes.data);
      setTransactions(transactionsRes.data);
      setExchangeRates(ratesRes.data);
      setSavingsAccounts(savingsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <UserLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="space-y-8" data-testid="user-dashboard">
        <div>
          <h1 className="text-4xl font-semibold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Dashboard
          </h1>
          <p className="mt-2 text-muted-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Welcome back! Here's your wallet overview.
          </p>
        </div>

        {(() => {
          // Compute conversion totals - only include wallets with valid conversions
          const walletsTotal = defaultCurrency ? wallets.reduce((s, w) => {
            const v = convertAmount(w.balance, w.currency, defaultCurrency, exchangeRates);
            // Only add if conversion is successful (not null), otherwise skip this wallet
            return v !== null ? s + v : s;
          }, 0) : null;
          const savingsTotal = defaultCurrency ? savingsAccounts
            .filter(a => a.status !== 'closed')
            .reduce((s, a) => {
              const v = convertAmount(a.balance, a.currency, defaultCurrency, exchangeRates);
              // Only add if conversion is successful (not null), otherwise skip
              return v !== null ? s + v : s;
            }, 0) : null;
          const grandTotal = (walletsTotal != null && savingsTotal != null) ? walletsTotal + savingsTotal : null;
          const totalGrowth = savingsAccounts.reduce((s, a) => s + (a.total_growth_earned || 0), 0);

          return (
            <>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                <Card className="border border-border hover:shadow-lg transition-shadow" data-testid="wallets-total-card">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Wallet Total</CardTitle>
                    <Wallet className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    {defaultCurrency && walletsTotal != null ? (
                      <>
                        <div className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="wallets-total-amount">
                          {walletsTotal.toFixed(2)} <span className="text-lg text-muted-foreground">{defaultCurrency}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">Across {wallets.length} wallet{wallets.length !== 1 ? 's' : ''}</p>
                      </>
                    ) : (
                      <>
                        <div className="text-lg font-semibold text-muted-foreground">Set default currency</div>
                        <p className="text-xs text-muted-foreground mt-1">Profile → Default Display Currency</p>
                      </>
                    )}
                  </CardContent>
                </Card>

                <Card className="border border-emerald-500/30 hover:shadow-lg transition-shadow bg-gradient-to-br from-emerald-500/5 to-transparent" data-testid="savings-total-card">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Sprout className="h-4 w-4 text-emerald-600" /> Savings Total
                    </CardTitle>
                    <PiggyBank className="h-4 w-4 text-emerald-600" />
                  </CardHeader>
                  <CardContent>
                    {defaultCurrency && savingsTotal != null ? (
                      <>
                        <div className="text-3xl font-semibold text-emerald-700 dark:text-emerald-400" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="savings-total-amount">
                          {savingsTotal.toFixed(2)} <span className="text-lg text-muted-foreground">{defaultCurrency}</span>
                        </div>
                        <p className="text-xs text-emerald-600 mt-1">
                          {savingsAccounts.length === 0 ? 'Start earning growth daily →' : `+${totalGrowth.toFixed(4)} earned · ${savingsAccounts.length} account${savingsAccounts.length !== 1 ? 's' : ''}`}
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="text-lg font-semibold text-muted-foreground">Smart Growth Savings</div>
                        <p className="text-xs text-muted-foreground mt-1">Set a default currency to see totals</p>
                      </>
                    )}
                  </CardContent>
                </Card>

                <Card className="border border-blue-500/30 hover:shadow-lg transition-shadow bg-gradient-to-br from-blue-500/5 to-transparent" data-testid="grand-total-card">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Grand Total</CardTitle>
                    <Coins className="h-4 w-4 text-blue-600" />
                  </CardHeader>
                  <CardContent>
                    {defaultCurrency && grandTotal != null ? (
                      <>
                        <div className="text-3xl font-semibold text-blue-700 dark:text-blue-400" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="grand-total-amount">
                          {grandTotal.toFixed(2)} <span className="text-lg text-muted-foreground">{defaultCurrency}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">Wallets + Savings combined</p>
                      </>
                    ) : (
                      <>
                        <div className="text-lg font-semibold text-muted-foreground">Grand Total</div>
                        <p className="text-xs text-muted-foreground mt-1">Set default currency</p>
                      </>
                    )}
                  </CardContent>
                </Card>

                <Card className="border border-border hover:shadow-lg transition-shadow">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Recent Activity</CardTitle>
                    <Activity className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{transactions.length}</div>
                    <p className="text-xs text-muted-foreground mt-1">{exchangeRates.length} exchange pair{exchangeRates.length !== 1 ? 's' : ''} available</p>
                  </CardContent>
                </Card>
              </div>

              {savingsAccounts.length > 0 && defaultCurrency && (
                <Card className="border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-transparent" data-testid="savings-widget">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      <Sprout className="h-5 w-5 text-emerald-600" /> Your Smart Growth Savings
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-3 md:grid-cols-2">
                      {savingsAccounts.filter(a => a.status !== 'closed').slice(0, 4).map(a => {
                        const equiv = defaultCurrency && defaultCurrency !== a.currency
                          ? convertAmount(a.balance, a.currency, defaultCurrency, exchangeRates)
                          : null;
                        return (
                          <div key={a.id} className="p-3 rounded-lg bg-background/60 border border-emerald-500/20" data-testid={`savings-widget-item-${a.id}`}>
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-sm font-medium">{a.plan_name}</p>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400">
                                +{a.total_growth_earned.toFixed(4)}
                              </span>
                            </div>
                            <p className="text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                              {a.balance.toFixed(4)} {a.currency}
                            </p>
                            {equiv != null && (
                              <p className="text-xs text-muted-foreground">≈ {equiv.toFixed(2)} {defaultCurrency}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">{a.days_saved} day{a.days_saved !== 1 ? 's' : ''} saved · {a.growth_percentage.toFixed(2)}% growth</p>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          );
        })()}

        <div className="grid gap-6 lg:grid-cols-2">
          <Card className="border border-border" data-testid="wallets-overview-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Wallet Balances</CardTitle>
                {wsConnected && (
                  <div className="flex items-center gap-1.5 text-xs text-green-600" data-testid="live-indicator">
                    <div className="h-2 w-2 rounded-full bg-green-600 animate-pulse"></div>
                    <span>Live</span>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {wallets.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No wallets available</p>
              ) : (
                <div className="space-y-4">
                  {wallets.map((wallet) => {
                    const equiv = defaultCurrency && defaultCurrency !== wallet.currency
                      ? convertAmount(wallet.balance, wallet.currency, defaultCurrency, exchangeRates)
                      : null;
                    return (
                    <div
                      key={wallet.id}
                      className={`flex items-center justify-between p-4 rounded-lg transition-all duration-500 ${
                        updatedCurrency === wallet.currency 
                          ? 'bg-green-100 dark:bg-green-950 ring-2 ring-green-500' 
                          : 'bg-muted/50 hover:bg-muted'
                      }`}
                      data-testid={`wallet-${wallet.currency}`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                          {wallet.currency.substring(0, 2)}
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{wallet.currency}</p>
                          <p className="text-xs text-muted-foreground">
                            {wallet.frozen ? 'Frozen' : wallet.locked ? 'Locked' : 'Active'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {formatCurrency(wallet.balance, wallet.currency)}
                        </p>
                        {equiv != null && (
                          <p className="text-xs text-muted-foreground" data-testid={`equiv-${wallet.currency}`}>
                            ≈ {equiv.toFixed(2)} {defaultCurrency}
                          </p>
                        )}
                        {wallet.locked_balance > 0 && (
                          <p className="text-xs text-muted-foreground">
                            Locked: {formatCurrency(wallet.locked_balance, wallet.currency)}
                          </p>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border border-border" data-testid="recent-transactions-card">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Recent Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              {transactions.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No transactions yet</p>
              ) : (
                <div className="space-y-4">
                  {transactions.slice(0, 5).map((tx) => (
                    <div
                      key={tx.id}
                      className="flex items-center justify-between p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                      data-testid={`transaction-${tx.id}`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                          tx.type === 'deposit' || tx.type === 'credit' || tx.type === 'voucher_redeem'
                            ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400'
                            : 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400'
                        }`}>
                          {tx.type === 'deposit' || tx.type === 'credit' || tx.type === 'voucher_redeem' ? (
                            <ArrowDownRight className="h-5 w-5" />
                          ) : (
                            <ArrowUpRight className="h-5 w-5" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-foreground capitalize">{tx.type.replace('_', ' ')}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(tx.created_at)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${
                          tx.type === 'deposit' || tx.type === 'credit' || tx.type === 'voucher_redeem'
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {tx.type === 'deposit' || tx.type === 'credit' || tx.type === 'voucher_redeem' ? '+' : '-'}
                          {formatCurrency(tx.amount, tx.currency)}
                        </p>
                        <p className="text-xs text-muted-foreground capitalize">{tx.status}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {exchangeRates.length > 0 && (
          <Card className="border border-border" data-testid="exchange-rates-card">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Current Exchange Rates</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {exchangeRates.slice(0, 6).map((rate) => (
                  <div
                    key={rate.id}
                    className="p-4 rounded-lg bg-muted/50 border border-border"
                    data-testid={`rate-${rate.from_currency}-${rate.to_currency}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-foreground">
                        {rate.from_currency} → {rate.to_currency}
                      </span>
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Buy:</span>
                        <span className="font-semibold text-foreground">{rate.buy_rate.toFixed(4)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Sell:</span>
                        <span className="font-semibold text-foreground">{rate.sell_rate.toFixed(4)}</span>
                      </div>
                      {rate.fee_percentage > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Fee:</span>
                          <span className="font-semibold text-foreground">{rate.fee_percentage}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </UserLayout>
  );
};
